import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  User, 
  Loader2,
  RefreshCw,
  Zap,
  XCircle,
  ArrowRight,
  Box,
  ShoppingCart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminBackorders() {
  const [backorders, setBackorders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fulfilling, setFulfilling] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showAutoFulfillDialog, setShowAutoFulfillDialog] = useState(false);
  
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchBackorders();
    fetchSummary();
  }, [statusFilter]);

  const fetchBackorders = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(`${API}/api/admin/backorders`, { ...authHeaders, params });
      setBackorders(res.data.backorders || []);
    } catch (err) {
      toast.error('Failed to load backorders');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/backorders/summary`, authHeaders);
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load summary');
    }
  };

  const fulfillBackorder = async (id) => {
    setFulfilling(true);
    try {
      await axios.post(`${API}/api/admin/backorders/${id}/fulfill`, {}, authHeaders);
      toast.success('Backorder fulfilled! Customer notified.');
      fetchBackorders();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fulfill backorder');
    } finally {
      setFulfilling(false);
    }
  };

  const cancelBackorder = async (id) => {
    try {
      await axios.post(`${API}/api/admin/backorders/${id}/cancel`, {}, authHeaders);
      toast.success('Backorder cancelled');
      fetchBackorders();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to cancel backorder');
    }
  };

  const autoFulfillAll = async () => {
    setFulfilling(true);
    try {
      const res = await axios.post(`${API}/api/admin/backorders/auto-fulfill`, {}, authHeaders);
      const { fulfilled, partially_fulfilled, skipped } = res.data;
      toast.success(`Auto-fulfilled: ${fulfilled} complete, ${partially_fulfilled} partial, ${skipped} skipped`);
      setShowAutoFulfillDialog(false);
      fetchBackorders();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to auto-fulfill backorders');
    } finally {
      setFulfilling(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      partially_fulfilled: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      fulfilled: 'bg-green-500/20 text-green-400 border-green-500/50',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/50'
    };
    return <Badge variant="outline" className={styles[status] || ''}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6 text-[#78BE20]" />
              Backorder Management
            </h1>
            <p className="text-muted-foreground">Manage pending backorders and fulfill when stock arrives</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchBackorders(); fetchSummary(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {summary?.can_fulfill > 0 && (
              <Button 
                onClick={() => setShowAutoFulfillDialog(true)}
                className="bg-[#78BE20] hover:bg-[#6aa819]"
              >
                <Zap className="w-4 h-4 mr-2" />
                Auto-Fulfill ({summary.can_fulfill})
              </Button>
            )}
          </div>
        </div>

        {/* Alert Banner */}
        {summary?.alert && (
          <div className="bg-[#78BE20]/20 border border-[#78BE20]/50 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-[#78BE20]" />
            <div className="flex-1">
              <p className="font-medium text-[#78BE20]">{summary.message}</p>
              <p className="text-sm text-muted-foreground">
                Click "Auto-Fulfill" to allocate stock using FIFO (First-In-First-Out)
              </p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary?.total_pending || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary?.can_fulfill || 0}</p>
                  <p className="text-sm text-muted-foreground">Ready to Fulfill</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Box className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{backorders.filter(b => b.status === 'partially_fulfilled').length}</p>
                  <p className="text-sm text-muted-foreground">Partial</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#78BE20]/20 rounded-lg">
                  <ShoppingCart className="w-5 h-5 text-[#78BE20]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{backorders.filter(b => b.status === 'fulfilled').length}</p>
                  <p className="text-sm text-muted-foreground">Fulfilled Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Backorders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partially_fulfilled">Partially Fulfilled</SelectItem>
              <SelectItem value="fulfilled">Fulfilled</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Backorders List */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle>Backorders ({backorders.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#78BE20]" />
              </div>
            ) : backorders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No backorders found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {backorders.map((bo) => (
                  <div 
                    key={bo.id} 
                    className={`p-4 rounded-lg border ${
                      bo.can_fulfill ? 'bg-[#78BE20]/10 border-[#78BE20]/30' : 'bg-zinc-800/50 border-zinc-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold">{bo.order_number}</span>
                          {getStatusBadge(bo.status)}
                          {bo.can_fulfill && (
                            <Badge className="bg-[#78BE20] text-white">Ready to Fulfill</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Product</p>
                            <p className="font-medium">{bo.product_name}</p>
                            <p className="text-xs text-muted-foreground">{bo.sku}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Quantity</p>
                            <p className="font-medium">
                              {bo.remaining_qty || bo.quantity} <span className="text-muted-foreground">/ {bo.quantity}</span>
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Customer</p>
                            <p className="font-medium flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {bo.customer_name}
                            </p>
                            <p className="text-xs text-muted-foreground">{bo.customer_email}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Stock Available</p>
                            <p className={`font-medium ${bo.current_stock >= (bo.remaining_qty || bo.quantity) ? 'text-green-400' : 'text-red-400'}`}>
                              {bo.current_stock} units
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Created: {new Date(bo.created_at).toLocaleString()}
                          {bo.fulfilled_at && ` • Fulfilled: ${new Date(bo.fulfilled_at).toLocaleString()}`}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {bo.status !== 'fulfilled' && bo.status !== 'cancelled' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => fulfillBackorder(bo.id)}
                              disabled={!bo.can_fulfill || fulfilling}
                              className="bg-[#78BE20] hover:bg-[#6aa819]"
                              data-testid={`fulfill-btn-${bo.id}`}
                            >
                              {fulfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Fulfill</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cancelBackorder(bo.id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-Fulfill Confirmation Dialog */}
        <Dialog open={showAutoFulfillDialog} onOpenChange={setShowAutoFulfillDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#78BE20]" />
                Auto-Fulfill Backorders
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                This will automatically allocate available stock to pending backorders using <strong>First-In-First-Out (FIFO)</strong> order.
              </p>
              {summary?.fulfillable_items && summary.fulfillable_items.length > 0 && (
                <div className="bg-zinc-800 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  <p className="text-sm font-medium mb-2">Will fulfill:</p>
                  {summary.fulfillable_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span>{item.product_name}</span>
                      <span className="text-[#78BE20]">{item.quantity} units → {item.customer_name}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-[#78BE20]/10 border border-[#78BE20]/20 rounded-lg p-3">
                <p className="text-sm text-[#78BE20]">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Customers will be notified via email when their items are fulfilled.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAutoFulfillDialog(false)}>Cancel</Button>
              <Button 
                onClick={autoFulfillAll} 
                disabled={fulfilling}
                className="bg-[#78BE20] hover:bg-[#6aa819]"
              >
                {fulfilling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Confirm Auto-Fulfill
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
